import { Slidy } from 'epic-slidy';
import { forEach, uniqueId } from 'lodash';
import anime from 'animejs';

import supportClipPath from 'utils/supportClipPath';

let instances = {};

export default class Slider {
  constructor(root) {
    this.root = root;
    this.list = this.root.querySelector('.u-slider__list');
    this.prevBtn = this.root.querySelector('.u-slider__pagination__prev');
    this.nextBtn = this.root.querySelector('.u-slider__pagination__next');
    this.videoSlides = this.root.querySelectorAll('.u-slider__slide.has-video');

    this.id = uniqueId('slider_');
    this.root.dataset.id = this.id;
    instances[this.id] = this;
    this.hasVideoPlaying = false;

    this.bind();
  }

  static getInstanceByElement(el) {
    if (!el.dataset || !el.dataset.id) {
      return;
    }

    return instances[el.dataset.id];
  }

  bind() {
    this.transition = this.transition.bind(this);
    this.basicTransition = this.basicTransition.bind(this);

    const transition = supportClipPath ? this.transition : this.basicTransition;

    this.slidy = new Slidy(this.list, {
      controls: false,
      transition,
      click: false,
      afterInit: function () {
        this.items[0].style.zIndex = 5;
        forEach(this.items, el => {
          el.style.display = 'block';
        });
      },
    });

    this.goPrev = this.goPrev.bind(this);
    this.goNext = this.goNext.bind(this);

    this.prevBtn.addEventListener('click', this.goPrev);
    this.nextBtn.addEventListener('click', this.goNext);

    // Add event for videos
    this.onVideoClick = this.onVideoClick.bind(this);
    this.videoSlides.forEach(slide => {
      const overlay = slide.querySelector('.u-slider__slide__video-overlay');

      if (overlay) {
        overlay.addEventListener('click', this.onVideoClick);
      }
    });
  }

  destroy() {}

  goPrev(e) {
    e && e.preventDefault();
    this.slidy.slidePrev();
  }

  goNext(e) {
    e && e.preventDefault();
    this.slidy.slideNext();
  }

  // eslint-disable-next-line no-unused-vars
  transition(currentSlide, newSlide, direction) {
    if (this.hasVideoPlaying) {
      this.deleteIframe();
    }

    currentSlide.style.zIndex = 5;
    newSlide.style.zIndex = 1;

    let zero = direction === 'next' ? 0 : 100;
    let full = direction === 'next' ? 100 : 0;

    let obj = {
      tl: zero,
      tr: full,
      br: full,
      bl: zero,
    };

    let coords = [
      [obj.tl, 0],
      [obj.tr, 0],
      [obj.br, 100],
      [obj.bl, 100],
    ];

    const tl = anime.timeline({
      autoplay: false,
    });

    const animation = new Promise(resolve => {
      tl.add({
        targets: obj,
        tl: [{ value: full, duration: 1000, delay: 75, easing: 'easeInOutQuart' }],
        tr: [{ value: full, duration: 1000, delay: 75, easing: 'easeInOutQuart' }],
        br: [{ value: full, duration: 1000, delay: 0, easing: 'easeInOutQuart' }],
        bl: [{ value: full, duration: 1000, delay: 0, easing: 'easeInOutQuart' }],
        update: () => {
          coords[0][0] = obj.tl;
          coords[1][0] = obj.tr;
          coords[2][0] = obj.br;
          coords[3][0] = obj.bl;

          this.applyClip(currentSlide, coords);
        },
      }).add({
        targets: newSlide.querySelector('img'),
        scale: 1.1,
        duration: 1000,
        easing: 'easeOutQuart',
        complete: () => {
          newSlide.style.zIndex = 5;
          currentSlide.style.zIndex = 0;
          currentSlide.querySelector('img').style.transform = '';
          this.cleanClip(currentSlide);
          resolve();
        },
        offset: '-=500',
      });
    });

    tl.play();
    tl.finished = animation;

    return animation;
  }

  basicTransition(currentSlide, newSlide, direction) {
    if (this.hasVideoPlaying) {
      this.deleteIframe();
    }

    currentSlide.style.zIndex = 5;
    newSlide.style.display = 'block';
    newSlide.style.zIndex = 4;

    const animation = anime({
      targets: currentSlide,
      opacity: 0,
      complete: () => {
        currentSlide.style.display = 'none';
        currentSlide.style.zIndex = 0;
        currentSlide.style.opacity = 1;
        newSlide.style.zIndex = 5;
      },
    });

    return animation.finished;
  }

  /*
  [
    ["0", "100%"],
    ["0", "100%"],
    ["0", "100%"],
    ["0", "100%"]
  ]
  */
  applyClip(el, coords) {
    let a = [];

    forEach(coords, c => {
      const [x, y] = c;
      a.push(`${x}% ${y}%`);
    });

    const str = `polygon(${a.join(',')})`;
    el.style.clipPath = str;
    el.style.webkitClipPath = str;
  }

  cleanClip(el) {
    el.style.clipPath = '';
    el.style.webkitClipPath = '';
  }

  createIframe(src) {
    const iframe = document.createElement('iframe');

    iframe.allowFullscreen = true;
    iframe.src = src;

    return iframe;
  }

  deleteIframe() {
    const slide = this.root.querySelector('.has-video.is-playing');

    if (!slide) {
      return;
    }

    const iframe = slide.querySelector('iframe');

    iframe.parentNode.removeChild(iframe);
    slide.classList.remove('is-playing');
    this.hasVideoPlaying = false;
  }

  onVideoClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const { currentTarget } = e;
    const slide = currentTarget.parentNode;
    const iframeContainer = slide.querySelector('.u-slider__slide__video-iframe');

    if (iframeContainer && iframeContainer.children.length === 0) {
      const iframe = this.createIframe(iframeContainer.dataset.src);

      iframeContainer.appendChild(iframe);
      slide.classList.add('is-playing');
      this.hasVideoPlaying = true;
    }
  }
}
